import { useCallback, useEffect, useState } from 'react';
import { WORKSPACE_TITLE_CLASS } from '../components/common/PageHeader';
import ReturnTrail from '../components/common/ReturnTrail';
import { Link, useParams } from 'react-router-dom';
import api from '../services/api';
import { useWebSocket } from '../contexts/WebSocketContext';
import ExternalTargets from '../components/linkbuilder/ExternalTargets';
import ScopePicker from '../components/linkbuilder/ScopePicker';
import SiteGraphPanel from '../components/linkbuilder/SiteGraphPanel';
import {
  NEUTRAL,
  sourceColors,
  sourceLabel,
  sourceSublabel,
} from '../components/linkbuilder/linkSourceColors';
import {
  siteFallout,
  sitesForScope,
  sourceHex,
} from '../components/linkbuilder/siteGraph';
import { removeScopeConfirm } from '../components/linkbuilder/linkFacts';
import type { DropMode } from '../components/linkbuilder/linkFacts';
import {
  EMPTY_HISTORY,
  clearHistory,
  commitRedo,
  isRedoable,
  replaceHistory,
  commitUndo,
  describeOp,
  peekRedo,
  peekUndo,
  record,
} from '../components/linkbuilder/linkHistory';
import type { History, LinkOp } from '../components/linkbuilder/linkHistory';
import type {
  ExternalPlacement,
  ExternalTarget,
  LinkEdge,
  LinkMap,
  LinkMapNodes,
} from '../types';
import type { WebSocketEvent } from '../types';

/**
 * One link map: its sources, its pages, and the links between them.
 *
 * Full-height like the boardroom and the subject workspace — `Layout` matches
 * `/link-builder/:id`, drops the page card and lifts the width cap, so this
 * owns its layout and must size itself to the viewport rather than growing the
 * document.
 *
 * Wide/narrow is a MEDIA QUERY, never a measurement: a ResizeObserver on a pane
 * whose width this component also controls is a feedback loop.
 */

// What a drop MEANS, remembered across sessions.
//
// A mode rather than a question at drop time. The gesture is repetitive — you
// link a dozen pages in a sitting — and a dialog on each one turns a drag into
// a form. Set it once and the drag is unambiguous again.
//
// One-way is the default because a reciprocal link is the shape search engines
// look at hardest: it should be something you chose, not what happens when you
// do not think about it.
const DROP_MODE_KEY = 'jcseo_linkbuilder_drop_mode';

export default function LinkBuilderWorkspace() {
  const { mapId } = useParams<{ mapId: string }>();
  const id = Number(mapId);
  const { subscribe } = useWebSocket();

  const [map, setMap] = useState<LinkMap | null>(null);
  const [nodes, setNodes] = useState<LinkMapNodes | null>(null);
  const [edges, setEdges] = useState<LinkEdge[]>([]);
  const [external, setExternal] = useState<ExternalPlacement[]>([]);
  const [targets, setTargets] = useState<ExternalTarget[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [removingScope, setRemovingScope] = useState<number | null>(null);
  /**
   * THE undo stack — one, for every verb on this screen.
   *
   * There were two: this one, and a snapshot restore offered in the notice bar
   * after a bulk verb. That split produced the worst possible failure — remove
   * a source, reach for the Undo button you can see, find it disabled, because
   * the thing you just did had been recorded somewhere else.
   */
  const [history, setHistory] = useState<History>(EMPTY_HISTORY);
  const [replaying, setReplaying] = useState(false);
  /**
   * The subject chip whose sites are lit up.
   *
   * The chips are SUBJECTS and the nodes are SITES — different groupings, since
   * a subject spans several sites and a site holds pages from several subjects.
   * Nothing on screen said which chip fed which node, so the only way to know
   * was to know already. Clicking a chip answers it.
   */
  const [litScopeId, setLitScopeId] = useState<number | null>(null);

  const [outboundOpen, setOutboundOpen] = useState(false);
  // Unwritten pages are INERT here — the AI is never offered them and nothing
  // can link to them, because they cannot deploy. Hidden by default so the list
  // is the pages you can act on; the count stays visible on every header and
  // the toggle says how many, so this is never a silent omission.
  const [dropMode, setDropMode] = useState<DropMode>(() =>
    localStorage.getItem(DROP_MODE_KEY) === 'both' ? 'both' : 'one-way',
  );
  useEffect(() => {
    localStorage.setItem(DROP_MODE_KEY, dropMode);
  }, [dropMode]);
  const [relinking, setRelinking] = useState(false);
  const loadMap = useCallback(async () => {
    if (!Number.isFinite(id)) {
      setError('That link map id is not a number.');
      setLoading(false);
      return;
    }
    try {
      const fetched = await api.getLinkMap(id);
      setMap(fetched);
      setRelinking(fetched.status === 'RELINKING');
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load this link map');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadNodes = useCallback(async () => {
    if (!Number.isFinite(id)) return;
    try {
      // Both together: the links ARE rows in the tree, so pages without their
      // edges would render a tree that is briefly missing half its content.
      const [fetchedNodes, fetchedEdges, fetchedTargets] = await Promise.all([
        api.getLinkMapNodes(id),
        api.listLinkMapEdges(id),
        // The addresses themselves, so they can sit in the tree as draggable
        // rows rather than living only inside the modal that made them.
        api.listExternalTargets(id),
      ]);
      setNodes(fetchedNodes);
      setEdges(fetchedEdges.edges);
      setExternal(fetchedEdges.external);
      setTargets(fetchedTargets);
      // Handed back as well as stored: an operation that has just created edges
      // needs their ids to be undoable, and `edges` state is not readable until
      // the next render.
      return fetchedEdges.edges;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load this map’s pages');
      return null;
    }
  }, [id]);

  useEffect(() => {
    loadMap();
  }, [loadMap]);

  useEffect(() => {
    if (map) loadNodes();
    // Re-resolved when the SOURCES change; edge counts refresh through
    // `refreshCounts` so a single drop does not refetch every page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map?.scopes.map((s) => s.id).join(','), map?.id]);

  useEffect(() => {
    return subscribe((event: WebSocketEvent) => {
      if (event.event_type !== 'link_map_relinked') return;
      if (event.link_map_id !== id) return;
      setRelinking(false);
      // Relink is the most destructive verb on this screen — it clears every
      // unlocked link before proposing new ones — so it is the one that most
      // needs a way back. Null on a map that had nothing to clear.
      // THE RELINK'S OWN WAY BACK, in the same stack as everything else.
      setHistory(
        event.snapshot_id == null
          ? clearHistory()
          : replaceHistory({
              kind: 'snapshot',
              snapshotId: event.snapshot_id,
              label: 'undo the relink',
            }),
      );
      setNotice(event.message || 'Relink finished.');
      loadMap();
      loadNodes();
    });
  }, [subscribe, id, loadMap, loadNodes]);


  const colors = map ? sourceColors(map.scopes) : new Map();

  const refreshCounts = useCallback(async () => {
    const fresh = await loadNodes();
    await loadMap();
    return fresh;
  }, [loadNodes, loadMap]);

  /**
   * Draw a link from the dock.
   *
   * `applyOp` does the create AND the refresh: the edge list feeds the dock's
   * already-linked markers and the map's edge count feeds the header. The site
   * graph is refetched by the panel, which owns it.
   */
  const handleLinkFromDock = async (ownerId: number, targetId: number) => {
    if (!map) return;
    const op = await applyOp(
      {
        kind: 'link',
        ownerId,
        targetId,
        bidirectional: dropMode === 'both',
        edgeIds: [],
      },
      'do',
    );
    setHistory((h) => record(h, op));
  };

  /**
   * Apply one operation, forwards or backwards, and hand back the op as it now
   * stands.
   *
   * The RETURN VALUE is the point. Undoing a link deletes rows; redoing it
   * inserts new ones with new ids, so the op that goes into the other stack
   * cannot be the one that came out of this one. Threading the updated op back
   * through `commitUndo` is what stops the stack accumulating ids that name
   * somebody else's link.
   */
  const applyOp = async (op: LinkOp, direction: 'do' | 'undo'): Promise<LinkOp> => {
    if (!map) return op;
    const forwards = direction === 'do';

    switch (op.kind) {
      case 'link': {
        if (forwards) {
          await api.createLinkEdge(map.id, {
            page_id: op.ownerId,
            linked_page_id: op.targetId,
            bidirectional: op.bidirectional,
          });
          const fresh = (await refreshCounts()) ?? [];
          // Found by their ENDS, not by trusting a returned id: a two-way link
          // creates two rows and the create call describes only one of them.
          const ids = fresh
            .filter(
              (e) =>
                (e.page_id === op.ownerId && e.linked_page_id === op.targetId) ||
                (op.bidirectional &&
                  e.page_id === op.targetId &&
                  e.linked_page_id === op.ownerId),
            )
            .map((e) => e.id);
          return { ...op, edgeIds: ids };
        }
        for (const edgeId of op.edgeIds) {
          await api.deleteLinkEdge(map.id, edgeId);
        }
        await refreshCounts();
        return { ...op, edgeIds: [] };
      }

      case 'unlink': {
        if (forwards) {
          await api.deleteLinkEdge(map.id, op.edge.id);
          await refreshCounts();
          return op;
        }
        const restored = await api.createLinkEdge(map.id, {
          page_id: op.edge.page_id,
          linked_page_id: op.edge.linked_page_id,
          ...(op.edge.anchor_text ? { anchor_text: op.edge.anchor_text } : {}),
        });
        // A locked link that comes back unlocked would be silently exposed to
        // the next relink, which is the one thing locking exists to prevent.
        if (op.edge.locked) {
          await api.updateLinkEdge(map.id, restored.id, { locked: true });
        }
        await refreshCounts();
        // The row is new, so the redo that follows has to name the new id.
        return { kind: 'unlink', edge: { ...op.edge, id: restored.id } };
      }

      case 'lock': {
        await api.updateLinkEdge(map.id, op.edgeId, {
          locked: forwards ? op.locked : !op.locked,
        });
        await refreshCounts();
        return op;
      }

      case 'place': {
        if (forwards) {
          await api.placeExternalTarget(map.id, op.externalTargetId, {
            page_ids: [op.pageId],
          });
        } else {
          await api.unplaceExternalTarget(map.id, op.externalTargetId, {
            page_ids: [op.pageId],
          });
        }
        await refreshCounts();
        return op;
      }

      case 'scope-removal': {
        if (forwards) {
          const updated = await api.removeLinkMapScope(map.id, op.scopeId);
          setMap(updated);
          // The removal that just happened describes how to reverse ITSELF; the
          // one stored from last time named links that no longer exist.
          return { ...op, undo: updated.undo ?? op.undo };
        }
        const result = await api.undoScopeRemoval(map.id, op.undo);
        setMap(result.map);
        // The restored scope is a NEW row. Redoing has to remove that one, not
        // the id that died with the original.
        const restored = result.map.scopes.find(
          (sc) =>
            sc.project_id === op.undo.project_id &&
            sc.root_topic_id === op.undo.root_topic_id,
        );
        await refreshCounts();
        return restored ? { ...op, scopeId: restored.id } : op;
      }

      case 'snapshot': {
        // Undo-only; `isRedoable` keeps it off the redo stack, so `forwards`
        // is never true here.
        await api.restoreLinkMapSnapshot(map.id, op.snapshotId);
        await refreshCounts();
        return op;
      }

      case 'unplace': {
        const { external_target_id: targetId, page_id: pageId } = op.placement;
        if (forwards) {
          await api.unplaceExternalTarget(map.id, targetId, { page_ids: [pageId] });
        } else {
          await api.placeExternalTarget(map.id, targetId, { page_ids: [pageId] });
        }
        await refreshCounts();
        return op;
      }
    }
  };

  const runHistory = async (direction: 'undo' | 'redo') => {
    if (!map || replaying) return;
    const op = direction === 'undo' ? peekUndo(history) : peekRedo(history);
    if (!op) return;
    setReplaying(true);
    try {
      const updated = await applyOp(op, direction === 'undo' ? 'undo' : 'do');
      setHistory((h) => {
        if (direction === 'redo') return commitRedo(h, updated);
        // A snapshot restore has no meaningful redo, so it leaves the stack
        // rather than moving across it — see `isRedoable`.
        return isRedoable(updated)
          ? commitUndo(h, updated)
          : { past: h.past.slice(0, -1), future: h.future };
      });
      setNotice(describeOp(op, direction).replace(/^(Undo|Redo): /, 'Done: '));
    } catch (e) {
      setError(e instanceof Error ? e.message : `Could not ${direction} that`);
    } finally {
      setReplaying(false);
    }
  };

  const handleToggleLock = async (edge: LinkEdge) => {
    if (!map) return;
    try {
      const op = await applyOp(
        { kind: 'lock', edgeId: edge.id, locked: !edge.locked },
        'do',
      );
      setHistory((h) => record(h, op));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change that link');
    }
  };

  const handlePlace = async (targetId: number, pageId: number) => {
    if (!map) return;
    const result = await api.placeExternalTarget(map.id, targetId, {
      page_ids: [pageId],
    });
    // Says which of the two things happened. Dropping a link onto a page that
    // already has it is a no-op, and without this the gesture looks broken in
    // exactly the case where nothing was wrong.
    if (result.placed === 0) {
      setNotice('That page already has this link.');
    } else {
      // Only a placement that HAPPENED is undoable. Recording a no-op would
      // make the next undo remove a link the user put there earlier.
      setHistory((h) =>
        record(h, { kind: 'place', externalTargetId: targetId, pageId }),
      );
    }
    await refreshCounts();
  };

  const handleUnplace = async (placement: ExternalPlacement) => {
    if (!map) return;
    try {
      await api.unplaceExternalTarget(map.id, placement.external_target_id, {
        page_ids: [placement.page_id],
      });
      setHistory((h) => record(h, { kind: 'unplace', placement }));
      await refreshCounts();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not remove that link');
    }
  };

  const handleUnlink = async (edge: LinkEdge) => {
    if (!map) return;
    try {
      // The WHOLE edge goes into the history, not its id: putting it back means
      // recreating anchor text and lock state, and after this call there is
      // nowhere left to read them from.
      const op = await applyOp({ kind: 'unlink', edge }, 'do');
      setHistory((h) => record(h, op));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not remove that link');
    }
  };

  const handleRelink = async () => {
    if (!map || relinking) return;
    if (
      !window.confirm(
        'Redraw this map’s links with AI?\n\n' +
          'Links you drew by hand and any you have locked are kept. ' +
          'Unlocked AI links are replaced.\n\nContinue?',
      )
    )
      return;
    setRelinking(true);
    setNotice('');
    // Relink can replace every unlocked edge, so every id in the history may
    // now name a different row. The stack is truncated here and the relink
    // records ITSELF when it finishes — see the websocket handler.
    setHistory(clearHistory());
    try {
      await api.relinkLinkMap(map.id);
    } catch (e) {
      setRelinking(false);
      setError(e instanceof Error ? e.message : 'Could not start the relink');
    }
  };

  const handleClearAll = async () => {
    if (!map || map.edge_count === 0) return;
    // Names what is about to be lost, including the part that was a decision
    // rather than a suggestion. "Clear all links?" alone would not distinguish
    // sweeping up an AI run from deleting an afternoon's work.
    if (
      !window.confirm(
        `Remove all ${map.edge_count} link${map.edge_count === 1 ? '' : 's'} from this map?\n\n` +
          'This includes any you drew yourself or locked. You can undo it ' +
          'straight afterwards.\n\nYour pages are not affected - only the links ' +
          'between them.\n\nContinue?',
      )
    )
      return;
    try {
      const result = await api.clearAllLinkEdges(map.id);
      // Nothing below survives this, and the clear itself becomes the one
      // thing Undo offers.
      setHistory(
        result.snapshot_id == null
          ? clearHistory()
          : replaceHistory({
              kind: 'snapshot',
              snapshotId: result.snapshot_id,
              label: 'put every link back',
            }),
      );
      setNotice(result.message);
      await refreshCounts();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not clear the links');
    }
  };

  /**
   * Take a source out of the map, from the chip that represents it.
   *
   * The same act the scope picker offers, at the place people reach for first:
   * you remove a thing by reaching for the thing, not by opening a dialog named
   * after the opposite verb. The picker keeps its Remove button — that is where
   * you are when you are auditing what is in the map — and both call the same
   * confirm builder so the warning cannot drift between them.
   */
  const handleRemoveScope = async (scopeId: number, label: string) => {
    if (!map || removingScope != null) return;
    if (
      !window.confirm(
        removeScopeConfirm(
          label,
          map.edge_count,
          siteFallout(nodes?.pages ?? [], scopeId),
        ),
      )
    )
      return;
    setRemovingScope(scopeId);
    try {
      const updated = await api.removeLinkMapScope(map.id, scopeId);
      // The removal deletes every edge touching that source, so ids below are
      // suspect — but the removal ITSELF is fully reversible, and it is the
      // thing the user will reach for Undo about. It becomes the only entry.
      setHistory(
        updated.undo
          ? replaceHistory({
              kind: 'scope-removal',
              scopeId,
              label,
              undo: updated.undo,
            })
          : clearHistory(),
      );
      // The refetch is not written here: the effects keyed on scope ids fire
      // because this list just got shorter.
      setMap(updated);
      setNotice(
        updated.undo
          ? `“${label}” removed, along with ${updated.undo.deleted_links} link${
              updated.undo.deleted_links === 1 ? '' : 's'
            }.`
          : `“${label}” removed from this map.`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not remove that source');
    } finally {
      setRemovingScope(null);
    }
  };

  const handleLockAll = async () => {
    if (!map) return;
    if (
      !window.confirm(
        'Lock every link in this map?\n\n' +
          'After this, Relink can only ADD links - it will not replace any. ' +
          'You can unlock them again at any time.\n\nContinue?',
      )
    )
      return;
    try {
      const result = await api.lockAllLinkEdges(map.id);
      // Every recorded lock state is now wrong; undoing one would flip a link
      // the user just deliberately locked.
      setHistory(clearHistory());
      setNotice(
        `${result.changed} link${result.changed === 1 ? '' : 's'} locked. ` +
          'Relink will not replace any of them.',
      );
      await refreshCounts();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not lock the links');
    }
  };

  const handleUnlockAll = async () => {
    if (!map) return;
    try {
      const result = await api.unlockAllLinkEdges(map.id);
      setHistory(clearHistory());
      setNotice(
        `${result.changed} AI link${result.changed === 1 ? '' : 's'} unlocked. ` +
          'Links you drew by hand were left alone.',
      );
      await refreshCounts();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not unlock the links');
    }
  };

  if (loading) {
    return <p className="p-6 text-sm text-gray-500 dark:text-gray-400">Loading…</p>;
  }

  if (!map) {
    return (
      <div className="p-6">
        <p className="text-sm text-red-600 dark:text-red-400">
          {error || 'Link map not found.'}
        </p>
        <Link
          to="/link-builder"
          className="mt-3 inline-block text-sm text-blue-600 dark:text-blue-400 hover:underline"
        >
          ← All link maps
        </Link>
      </div>
    );
  }

  const hasScopes = map.scopes.length > 0;
  const litScope = map.scopes.find((s) => s.id === litScopeId) ?? null;
  const undoOp = peekUndo(history);
  const redoOp = peekRedo(history);

  return (
    <div className="h-[calc(100vh-7rem)] flex flex-col px-4 py-3">
      {/* ── header ─────────────────────────────────────────────────────── */}
      <div className="shrink-0">
        {/* Where you are, left. The way out, right — the one placement every
            screen uses.
            These two are different questions and both belong here: "All link
            maps" is STRUCTURAL, the list this map lives in; ReturnTrail is
            where you actually came FROM, which for a deep link is somewhere
            else entirely. It renders nothing when there is no trail, so the
            common case is unchanged. */}
        <div className="flex items-start justify-between gap-4">
          <Link
            to="/link-builder"
            className="text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
          >
            ← All link maps
          </Link>
          <ReturnTrail className="mb-0 shrink-0" />
        </div>

        <div className="mt-1 flex items-center gap-3 flex-wrap">
          <h1 className={WORKSPACE_TITLE_CLASS}>
            {map.name}
          </h1>
          {relinking && (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300">
              <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
              </svg>
              Relinking
            </span>
          )}

          <div className="ml-auto flex items-center gap-2">
            {/* Per-operation undo, distinct from the snapshot Undo that appears
                in the notice bar after a bulk verb. These are always here and
                step back one link at a time; that one appears once, right
                after the thing you might regret, and restores a whole graph. */}
            <div className="inline-flex mr-1 rounded border border-gray-300 dark:border-gray-600 overflow-hidden">
              <button
                onClick={() => runHistory('undo')}
                disabled={replaying || undoOp === null}
                className="px-2 py-1.5 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                title={
                  undoOp
                    ? describeOp(undoOp, 'undo')
                    : 'Nothing to undo - draw or remove a link first'
                }
                aria-label="Undo the last link change"
              >
                {/* SVG, not ↶. The arrow glyphs render as tofu boxes in Safari,
                    which on a control whose whole job is inspiring confidence
                    reads as something broken. */}
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 15L3 9l6-6" />
                  <path d="M3 9h12a6 6 0 010 12h-3" />
                </svg>
              </button>
              <button
                onClick={() => runHistory('redo')}
                disabled={replaying || redoOp === null}
                className="px-2 py-1.5 text-gray-600 dark:text-gray-300 border-l border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                title={
                  redoOp ? describeOp(redoOp, 'redo') : 'Nothing to redo'
                }
                aria-label="Redo the last undone link change"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 15l6-6-6-6" />
                  <path d="M21 9H9a6 6 0 000 12h3" />
                </svg>
              </button>
            </div>
            <button
              onClick={handleRelink}
              disabled={relinking || !hasScopes}
              className="px-3 py-1.5 text-xs font-medium rounded bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              title={
                hasScopes ? undefined : 'Add a subject before relinking'
              }
            >
              {relinking ? 'Relinking…' : 'Relink'}
            </button>
            <button
              onClick={handleLockAll}
              disabled={map.edge_count === 0}
              className="px-2 py-1.5 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40"
            >
              Lock all
            </button>
            {/* Sits NEXT TO Lock all, always. Lock all is a one-way door
                otherwise, and a door with no visible way back is one people
                stop opening. */}
            <button
              onClick={handleUnlockAll}
              disabled={map.edge_count === 0}
              className="px-2 py-1.5 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40"
            >
              Unlock all
            </button>
            <button
              onClick={handleClearAll}
              disabled={map.edge_count === 0}
              className="px-2 py-1.5 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-red-50 hover:text-red-700 hover:border-red-300 dark:hover:bg-red-900/30 dark:hover:text-red-300 disabled:opacity-40 disabled:cursor-not-allowed"
              title="Remove every link in this map. Undoable."
            >
              Clear links
            </button>
            {/* Separate from Relink, because placing an external link is
                DISTRIBUTION and relinking is inference. Putting it in the same
                flow would suggest the AI decides where these go. */}
            <button
              onClick={() => setOutboundOpen(true)}
              className="px-2 py-1.5 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              title="Addresses outside Lumnian - a partner, or another site of yours. Add one here, then drag it onto pages from the strip below the graph."
            >
              External links…
            </button>
          </div>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {map.scopes.map((scope) => {
            const c = colors.get(scope.id) ?? NEUTRAL;
            const sub = sourceSublabel(scope);
            const label = sourceLabel(scope);
            const lit = litScopeId === scope.id;
            return (
              <span
                key={scope.id}
                className={`inline-flex items-center gap-1.5 pl-1 pr-1 py-1 rounded text-xs ${c.soft} ${c.text} ${
                  lit ? 'ring-2 ring-offset-1 ring-current' : ''
                }`}
                title={sub ? `${sub} → ${label}` : label}
              >
                {/* CLICKABLE, and that is the only affordance saying these two
                      rows of colour are about different things. */}
                <button
                  onClick={() => setLitScopeId(lit ? null : scope.id)}
                  className="inline-flex items-center gap-1.5 px-1 rounded hover:bg-black/5 dark:hover:bg-white/10"
                  title={`Show which sites ${label}’s pages are on`}
                  aria-pressed={lit}
                >
                  <span className={`w-2 h-2 rounded-full ${c.dot}`} />
                  {label}
                </button>
                {/* The chip IS the source, so removing it belongs here. Before
                    this, the only way out was a dialog opened by a button
                    labelled "+ Add" — the affordance for removal was the word
                    for the opposite. */}
                <button
                  onClick={() => handleRemoveScope(scope.id, label)}
                  disabled={removingScope != null}
                  className="ml-0.5 p-0.5 rounded hover:bg-black/10 dark:hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
                  title={`Remove ${label} from this map`}
                  aria-label={`Remove ${label} from this map`}
                >
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                    strokeLinecap="round"
                  >
                    <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </button>
              </span>
            );
          })}
          {/* "+ Add" was the whole vocabulary for a control that also removes,
              renames nothing and audits the map. Once there is something in
              here, this button MANAGES sources; the empty state is the only
              time its job is purely to add one. */}
          <button
            onClick={() => setPickerOpen(true)}
            className={`px-2 py-1 text-xs rounded border text-gray-600 dark:text-gray-300 ${
              hasScopes
                ? 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                : 'border-dashed border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
            }`}
          >
            {hasScopes ? 'Sources…' : '+ Add a subject'}
          </button>
        </div>

        {error && (
          <div className="mt-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-3 py-1.5 rounded text-sm">
            {error}
          </div>
        )}
        {notice && (
          <div className="mt-2 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 px-3 py-1.5 rounded text-sm flex items-center gap-2">
            <span className="flex-1">{notice}</span>
            {/* Where you are already looking, immediately after the thing you
                might regret. A history panel three clicks away is an undo
                nobody reaches in the second when they want it. */}
            <button
              onClick={() => setNotice('')}
              className="icon-btn shrink-0 text-blue-400 hover:text-blue-700 dark:hover:text-blue-200"
            >
              ×
            </button>
          </div>
        )}
        {nodes?.truncated && (
          // NEVER a silent truncation. A list that quietly stops reads as
          // "these are all my pages", and people judge coverage from it.
          <div className="mt-2 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 px-3 py-1.5 rounded text-sm">
            Showing {nodes.pages.length} of {nodes.total} pages. Narrow the map’s
            sources to see the rest.
          </div>
        )}
      </div>

      {/* ── body ───────────────────────────────────────────────────────── */}
      {hasScopes ? (
        <div className="flex-1 min-h-0 mt-3 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800 flex flex-col">
          <SiteGraphPanel
            mapId={map.id}
            // One fetch feeds both the graph and the dock: every page carries
            // its site, so the columns cost no request of their own.
            pages={nodes?.pages ?? []}
            edges={edges}
            // The same key the tree reload watches, for the same reason: adding
            // or removing a source changes which pages — and therefore which
            // sites — the map covers.
            sourcesKey={map.scopes.map((s) => s.id).join(',')}
            targets={targets}
            externalPlacements={external}
            onPlace={handlePlace}
            onUnplace={handleUnplace}
            litSites={
              litScope
                ? sitesForScope(nodes?.pages ?? [], litScope.id)
                : null
            }
            litColor={litScope ? sourceHex(litScope.order) : undefined}
            litLabel={litScope ? sourceLabel(litScope) : undefined}
            onClearLit={() => setLitScopeId(null)}
            scopeOrder={
              new Map(map.scopes.map((sc) => [sc.id, sc.order]))
            }
            truncated={nodes?.truncated}
            dropMode={dropMode}
            onDropModeChange={setDropMode}
            onLink={handleLinkFromDock}
            onUnlink={handleUnlink}
            onToggleLock={handleToggleLock}
            // The tree is gone, so a page click opens the page itself rather
            // than hopping to another lens to select it there.
            onOpenPage={(pageId) => {
              const page = nodes?.pages.find((p) => p.id === pageId) ?? null;
              if (page?.public_path) window.open(page.public_path, '_blank');
            }}
          />
        </div>
      ) : (
        <div className="flex-1 min-h-0 mt-3 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg flex items-center justify-center">
          <div className="text-center max-w-md px-6">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              This map has no sources yet.
            </p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Add a subject - or one branch of one - and its pages appear here ready to
              link. Add two subjects that publish to different websites to build links
              between them.
            </p>
            <button
              onClick={() => setPickerOpen(true)}
              className="mt-4 px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
            >
              Add a subject
            </button>
          </div>
        </div>
      )}

      {pickerOpen && (
        <ScopePicker
            map={map}
            onClose={() => setPickerOpen(false)}
            onChanged={setMap}
            pages={nodes?.pages ?? []}
            onScopeRemoved={(undo, scopeId, label) => {
              setHistory(
                undo
                  ? replaceHistory({ kind: 'scope-removal', scopeId, label, undo })
                  : clearHistory(),
              );
                    }}
          />
      )}

      {outboundOpen && (
        <ExternalTargets
          mapId={map.id}
          pages={nodes?.pages ?? []}
          placements={external}
          onUnplace={handleUnplace}
          onClose={() => setOutboundOpen(false)}
          onChanged={refreshCounts}
        />
      )}
    </div>
  );
}
